import { useState, type FormEvent } from 'react';
import { t } from '../../i18n';
import { formatDate } from '@werkdelta/shared';
import { Feld, Fehler, RechtsHinweis } from '../../components/ui';
import { useEntityActions } from '../../hooks';
import type { StepProps } from '../CasePage';

const CONTRACT_TYPES = ['WERKVERTRAG', 'GRUNDSTUECKKAUF', 'PLANERVERTRAG', 'GEMISCHT'];
const SIA = ['JA', 'NEIN', 'UNKLAR'];

/**
 * Schritt 2: Vertragsbeziehungen mit Rechtsprofil und Zeitachse (Auftrag 3.2).
 * Die Software zeigt Hinweise, nie Rechtsentscheide.
 */
export function Step2Vertraege({ caseId, data, reload, readonly }: StepProps) {
  const actions = useEntityActions(caseId, 'contract-relations', reload);
  const [form, setForm] = useState(leeresFormular());
  const [editId, setEditId] = useState<string | null>(null);

  function leeresFormular() {
    return {
      label: '',
      contractType: 'WERKVERTRAG',
      contractDate: '',
      sia118: 'UNKLAR',
      sia118Edition: '',
      sia118Deviations: '',
      partyAId: '',
      partyBId: '',
      acceptanceDate: '',
      notes: '',
    };
  }

  function payload() {
    return {
      label: form.label,
      contractType: form.contractType,
      contractDate: form.contractDate || undefined,
      sia118: form.sia118,
      sia118Edition: form.sia118Edition || undefined,
      sia118Deviations: form.sia118Deviations || undefined,
      partyAId: form.partyAId || undefined,
      partyBId: form.partyBId || undefined,
      acceptanceDate: form.acceptanceDate || undefined,
      notes: form.notes || undefined,
    };
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    const ok = editId ? await actions.update(editId, payload()) : await actions.create(payload());
    if (ok) {
      setForm(leeresFormular());
      setEditId(null);
    }
  }

  function startEdit(rel: any) {
    setEditId(rel.id);
    setForm({
      label: rel.label ?? '',
      contractType: rel.contractType,
      contractDate: rel.contractDate ? rel.contractDate.slice(0, 10) : '',
      sia118: rel.sia118,
      sia118Edition: rel.sia118Edition ?? '',
      sia118Deviations: rel.sia118Deviations ?? '',
      partyAId: rel.partyAId ?? '',
      partyBId: rel.partyBId ?? '',
      acceptanceDate: rel.acceptanceDate ? rel.acceptanceDate.slice(0, 10) : '',
      notes: rel.notes ?? '',
    });
  }

  return (
    <>
      <section className="karte">
        <h2>{t('step2.title')}</h2>
        <p className="erklaerung">{t('step2.intro')}</p>
        {data.contractRelations.length === 0 && (
          <div className="leer-hinweis">{t('step2.empty')}</div>
        )}
        {data.contractRelations.map((rel: any) => (
          <div className="eintrag" key={rel.id}>
            <div className="eintrag-kopf">
              <strong>{rel.label}</strong>
              <span className="badge">{t(`contract.type.${rel.contractType}`)}</span>
              {rel.regimeCandidate && (
                <span className="badge tuerkis">{t(`regime.${rel.regimeCandidate}`)}</span>
              )}
              {!readonly && (
                <span style={{ display: 'flex', gap: '0.4rem' }}>
                  <button
                    type="button"
                    className="knopf-sekundaer knopf-klein"
                    onClick={() => startEdit(rel)}
                  >
                    {t('common.edit')}
                  </button>
                  <button
                    type="button"
                    className="knopf-sekundaer knopf-klein"
                    onClick={() => actions.remove(rel.id)}
                  >
                    {t('common.delete')}
                  </button>
                </span>
              )}
            </div>
            <p className="meta">
              {rel.contractDate
                ? t('step2.contractDateLabel', { date: formatDate(rel.contractDate) })
                : t('step2.contractDateMissing')}
              {' · '}
              {t('step2.sia118Label')}: {t(`common.${rel.sia118 === 'JA' ? 'yes' : rel.sia118 === 'NEIN' ? 'no' : 'unclear'}`)}
              {rel.sia118Edition ? ` (${rel.sia118Edition})` : ''}
              {rel.acceptanceDate
                ? ` · ${t('step2.acceptanceLabel')}: ${formatDate(rel.acceptanceDate)}`
                : ''}
            </p>
            {rel.hints?.map((h: { key: string; params?: Record<string, string> }, i: number) => (
              <RechtsHinweis key={i} text={t(h.key, h.params)} />
            ))}
          </div>
        ))}
      </section>

      {!readonly && (
        <section className="karte">
          <h3>{editId ? t('step2.editRelation') : t('step2.addRelation')}</h3>
          <form onSubmit={submit}>
            <div className="feld-zeile">
              <Feld label={t('step2.label')} required error={actions.fieldErrors['label']}>
                <input
                  value={form.label}
                  onChange={(e) => setForm({ ...form, label: e.target.value })}
                  required
                />
              </Feld>
              <Feld label={t('step2.contractType')} required>
                <select
                  value={form.contractType}
                  onChange={(e) => setForm({ ...form, contractType: e.target.value })}
                >
                  {CONTRACT_TYPES.map((ct) => (
                    <option key={ct} value={ct}>
                      {t(`contract.type.${ct}`)}
                    </option>
                  ))}
                </select>
              </Feld>
            </div>
            <div className="feld-zeile">
              <Feld
                label={t('step2.contractDate')}
                hint={t('step2.contractDateHint')}
              >
                <input
                  type="date"
                  value={form.contractDate}
                  onChange={(e) => setForm({ ...form, contractDate: e.target.value })}
                />
              </Feld>
              <Feld label={t('step2.acceptance')} hint={t('step2.acceptanceHint')}>
                <input
                  type="date"
                  value={form.acceptanceDate}
                  onChange={(e) => setForm({ ...form, acceptanceDate: e.target.value })}
                />
              </Feld>
            </div>
            <fieldset>
              <legend>{t('step2.sia118Legend')}</legend>
              <div className="feld-zeile">
                <Feld label={t('step2.sia118Included')} required>
                  <select
                    value={form.sia118}
                    onChange={(e) => setForm({ ...form, sia118: e.target.value })}
                  >
                    {SIA.map((s) => (
                      <option key={s} value={s}>
                        {t(`common.${s === 'JA' ? 'yes' : s === 'NEIN' ? 'no' : 'unclear'}`)}
                      </option>
                    ))}
                  </select>
                </Feld>
                <Feld label={t('step2.sia118Edition')}>
                  <input
                    value={form.sia118Edition}
                    onChange={(e) => setForm({ ...form, sia118Edition: e.target.value })}
                  />
                </Feld>
              </div>
              <Feld label={t('step2.sia118Deviations')}>
                <textarea
                  value={form.sia118Deviations}
                  onChange={(e) => setForm({ ...form, sia118Deviations: e.target.value })}
                />
              </Feld>
            </fieldset>
            <div className="feld-zeile">
              <Feld label={t('step2.partyA')}>
                <select
                  value={form.partyAId}
                  onChange={(e) => setForm({ ...form, partyAId: e.target.value })}
                >
                  <option value="">—</option>
                  {data.parties.map((p: any) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </Feld>
              <Feld label={t('step2.partyB')}>
                <select
                  value={form.partyBId}
                  onChange={(e) => setForm({ ...form, partyBId: e.target.value })}
                >
                  <option value="">—</option>
                  {data.parties.map((p: any) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </Feld>
            </div>
            <Feld label={t('step2.notes')}>
              <textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </Feld>
            <Fehler code={actions.errorCode} />
            <div className="aktionen">
              <button type="submit" className="knopf-primaer">
                {editId ? t('common.save') : t('common.add')}
              </button>
              {editId && (
                <button
                  type="button"
                  className="knopf-sekundaer"
                  onClick={() => {
                    setEditId(null);
                    setForm(leeresFormular());
                  }}
                >
                  {t('common.cancel')}
                </button>
              )}
            </div>
          </form>
        </section>
      )}
    </>
  );
}
